import { useMemo, useState } from 'react';
import { Button, Field, Notice, Spinner } from '../components/ui';
import { Dialog } from '../components/Dialog';
import { DirectionTag } from '../components/ScoreMode';
import { ApiError, downloadFile } from '../lib/api';
import {
  dayMonth,
  hours,
  useArchiveTarget,
  useDeleteSession,
  useDevelopmentOptions,
  useSaveSession,
  useSessions,
  useSaveTarget,
  useTargets,
  useTeamLog,
  type Audience,
  type DevelopmentOptions,
  type PdSession,
  type Target,
  type TargetInput,
} from '../lib/development';
import '../components/Form.css';
import './Page.css';
import './Hub.css';
import './Development.css';

/**
 * Development: the term's PD targets. Leaders set a few for everyone or a
 * group, each tracked through framework indicators and reflected on with a
 * model; teachers work on them and reflect in the app. Leaders see who has
 * reflected, what they tried, what people chose to share, and the team trend.
 */
export function DevelopmentPage() {
  const targets = useTargets();
  const options = useDevelopmentOptions();
  const [editing, setEditing] = useState<Target | 'new' | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const active = targets.data?.targets.filter((t) => t.status === 'active') ?? [];
  const archived = targets.data?.targets.filter((t) => t.status === 'archived') ?? [];

  return (
    <div className="page">
      <header className="page-head dev-head">
        <div>
          <h1>Development</h1>
          <p className="muted">
            A few targets a term. Teachers work on them in the app and reflect as they go, with their lesson evidence alongside. Their reflections are
            theirs: you see how many have reflected, and what people choose to share.
          </p>
        </div>
        <Button onClick={() => setEditing('new')} disabled={!options.data}>
          New target
        </Button>
      </header>

      {targets.isLoading ? (
        <Spinner />
      ) : targets.isError ? (
        <Notice tone="error">We couldn&rsquo;t load your targets. Refresh the page to try again.</Notice>
      ) : active.length === 0 ? (
        <section className="panel panel-tint dev-empty">
          <h2>Set your first target</h2>
          <p className="muted">
            Pick something the team will work on this term, such as one of your priority areas. Choose who it&rsquo;s for, the framework indicators that
            show it, and how people will reflect. Everyone it&rsquo;s for sees it in the app, with a nudge at each check-in.
          </p>
          <div>
            <Button onClick={() => setEditing('new')} disabled={!options.data}>
              New target
            </Button>
          </div>
        </section>
      ) : (
        <div className="dev-targets">
          {active.map((t) => (
            <TargetCard key={t.id} t={t} minGroup={targets.data?.minGroup ?? 5} onEdit={() => setEditing(t)} />
          ))}
        </div>
      )}

      {archived.length ? (
        <section className="hub-section">
          <button type="button" className="hub-link" onClick={() => setShowArchived((v) => !v)} aria-expanded={showArchived}>
            {showArchived ? 'Hide' : 'Show'} {archived.length} earlier {archived.length === 1 ? 'target' : 'targets'}
          </button>
          {showArchived ? (
            <div className="dev-targets">
              {archived.map((t) => (
                <TargetCard key={t.id} t={t} minGroup={targets.data?.minGroup ?? 5} onEdit={() => setEditing(t)} />
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      <Sessions targets={active} />

      <TeamLog />

      {editing && options.data ? (
        <TargetEditor options={options.data} target={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      ) : null}
    </div>
  );
}

const TRIED_LABEL: Record<string, string> = { yes: 'Tried it', partly: 'Partly', not_yet: 'Not yet' };

function TargetCard({ t, minGroup, onEdit }: { t: Target; minGroup: number; onEdit: () => void }) {
  const archive = useArchiveTarget();
  const [openShared, setOpenShared] = useState(false);
  const prompts = t.model?.prompts ?? [];
  return (
    <article className="panel dev-target">
      <div className="dev-target-top">
        <div className="dev-target-title">
          <h2>{t.title}</h2>
          <p className="faint">
            {t.audienceLabel} ({t.audienceSize} {t.audienceSize === 1 ? 'person' : 'people'}) &middot; {dayMonth(t.startsOn)} to {dayMonth(t.endsOn)}
            {t.model ? <> &middot; {t.model.name}</> : null}
          </p>
        </div>
        <div className="dev-target-actions">
          <Button variant="secondary" onClick={onEdit}>
            Edit
          </Button>
          <Button variant="ghost" onClick={() => archive.mutate({ id: t.id, archived: t.status === 'active' })} loading={archive.isPending}>
            {t.status === 'active' ? 'Archive' : 'Restore'}
          </Button>
        </div>
      </div>
      {t.why ? <p className="dev-why">{t.why}</p> : null}

      <div className="dev-checkpoints">
        {t.checkpoints.map((c) => {
          const pct = t.audienceSize ? Math.round((c.reflected / t.audienceSize) * 100) : 0;
          return (
            <div key={c.id} className={`dev-cp${c.state === 'upcoming' ? ' is-upcoming' : ''}`}>
              <div className="dev-cp-head">
                <strong>{c.label}</strong>
                <span className="faint">{dayMonth(c.dueOn)}</span>
              </div>
              {c.state === 'upcoming' ? (
                <p className="faint">Opens on {dayMonth(c.dueOn)}</p>
              ) : (
                <>
                  <p className="dev-cp-count">
                    <span className="dev-cp-num">{c.reflected}</span> of {t.audienceSize} reflected
                  </p>
                  <div className="dev-bar" aria-hidden="true">
                    <span style={{ width: `${pct}%` }} />
                  </div>
                  {c.tried ? (
                    <p className="faint dev-cp-tried">
                      {c.tried.yes} tried it &middot; {c.tried.partly} partly &middot; {c.tried.notYet} not yet
                    </p>
                  ) : c.reflected ? (
                    <p className="faint dev-cp-tried">How people got on shows once 5 or more have reflected.</p>
                  ) : null}
                </>
              )}
            </div>
          );
        })}
      </div>

      {t.indicators.length ? (
        <div className="dev-indicators">
          <h3 className="dev-sub">Across the team this term</h3>
          <ul>
            {t.indicators.map((i) => (
              <li key={i.id}>
                <span>{i.name}</span>
                {i.teachers < minGroup ? (
                  <span className="faint">Shows with {minGroup}+ teachers</span>
                ) : (
                  <span className="dev-ind-right">
                    {i.average != null ? <span className="hub-ind-score">{i.average.toFixed(1)}</span> : null}
                    <DirectionTag d={i.direction} />
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="dev-shared">
        <button type="button" className="hub-link" onClick={() => setOpenShared((v) => !v)} aria-expanded={openShared} disabled={!t.shared.length}>
          {t.shared.length
            ? `${openShared ? 'Hide' : 'Read'} ${t.shared.length} shared ${t.shared.length === 1 ? 'reflection' : 'reflections'}`
            : 'No reflections shared with leaders yet'}
        </button>
        {openShared ? (
          <div className="dev-shared-list">
            {t.shared.map((r) => (
              <div key={r.id} className="dev-reflection">
                <div className="dev-reflection-head">
                  <strong>{r.name}</strong>
                  <span className="faint">
                    {t.checkpoints.find((c) => c.id === r.checkpointId)?.label ?? 'Check-in'} &middot; {dayMonth(r.at.slice(0, 10))}
                  </span>
                  {r.tried ? <span className="chip">{TRIED_LABEL[r.tried] ?? r.tried}</span> : null}
                </div>
                <dl>
                  {prompts
                    .filter((p) => r.answers[p.key])
                    .map((p) => (
                      <div key={p.key}>
                        <dt>{p.question}</dt>
                        <dd>{r.answers[p.key]}</dd>
                      </div>
                    ))}
                </dl>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </article>
  );
}

/** INSET days and twilights: what's coming, and how many logged that they were there. */
function Sessions({ targets }: { targets: Target[] }) {
  const sessions = useSessions();
  const [editing, setEditing] = useState<PdSession | 'new' | null>(null);
  const del = useDeleteSession();
  const list = sessions.data ?? [];
  const todayYmd = new Date().toISOString().slice(0, 10);
  return (
    <section className="hub-section">
      <div className="hub-section-head dev-row-head">
        <div>
          <h2>Sessions</h2>
          <p className="muted">INSET days and twilights. Staff see them in the app and log that they were there in one tap; it goes into their PD log.</p>
        </div>
        <Button variant="secondary" onClick={() => setEditing('new')}>
          Add a session
        </Button>
      </div>
      {list.length ? (
        <div className="panel">
          <ul className="dev-sessions">
            {list.map((x) => (
              <li key={x.id}>
                <div className="dev-session-date">
                  <span>{new Date(`${x.happensOn}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric' })}</span>
                  <span>{new Date(`${x.happensOn}T12:00:00Z`).toLocaleDateString('en-GB', { month: 'short' })}</span>
                </div>
                <div className="dev-session-body">
                  <strong>{x.title}</strong>
                  <span className="faint">
                    {hours(x.minutes)}
                    {x.location ? ` · ${x.location}` : ''}
                    {x.target ? ` · ${x.target.title}` : ''}
                  </span>
                </div>
                <span className="faint dev-session-attended">{x.happensOn <= todayYmd ? `${x.attended} logged it` : 'Coming up'}</span>
                <span className="dev-session-actions">
                  <button type="button" className="hub-link" onClick={() => setEditing(x)}>
                    Edit
                  </button>
                  <button type="button" className="hub-link" onClick={() => window.confirm(`Delete “${x.title}”? Anyone who logged it keeps the entry.`) && del.mutate(x.id)}>
                    Delete
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="faint">No sessions yet.</p>
      )}
      {editing ? <SessionEditor session={editing === 'new' ? null : editing} targets={targets} onClose={() => setEditing(null)} /> : null}
    </section>
  );
}

const SESSION_LENGTHS = [
  { minutes: 45, label: '45 minutes' },
  { minutes: 60, label: '1 hour' },
  { minutes: 90, label: '1½ hours' },
  { minutes: 120, label: '2 hours' },
  { minutes: 180, label: 'Half day' },
  { minutes: 360, label: 'Full day' },
];

function SessionEditor({ session, targets, onClose }: { session: PdSession | null; targets: Target[]; onClose: () => void }) {
  const save = useSaveSession();
  const [title, setTitle] = useState(session?.title ?? '');
  const [happensOn, setHappensOn] = useState(session?.happensOn ?? new Date().toISOString().slice(0, 10));
  const [minutes, setMinutes] = useState(session?.minutes ?? 90);
  const [location, setLocation] = useState(session?.location ?? '');
  const [details, setDetails] = useState(session?.details ?? '');
  const [targetId, setTargetId] = useState<string | null>(session?.target?.id ?? null);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    if (!title.trim()) {
      setError('Give the session a title.');
      return;
    }
    try {
      await save.mutateAsync({ id: session?.id, input: { title, happensOn, minutes, location, details, targetId } });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That didn’t save. Try again.');
    }
  };
  return (
    <Dialog open onClose={onClose} title={session ? 'Edit session' : 'Add a session'}>
      <div className="dev-editor">
        <Field id="ses-title" label="Session" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Twilight: questioning" maxLength={160} />
        <div className="dev-dates">
          <Field id="ses-date" label="Date" type="date" value={happensOn} onChange={(e) => setHappensOn(e.target.value)} />
          <div className="field">
            <label htmlFor="ses-length">Length</label>
            <select id="ses-length" className="select" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
              {SESSION_LENGTHS.map((l) => (
                <option key={l.minutes} value={l.minutes}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <Field id="ses-where" label="Where (optional)" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Library" maxLength={120} />
        {targets.length ? (
          <div className="field">
            <label htmlFor="ses-target">Part of a target (optional)</label>
            <select id="ses-target" className="select" value={targetId ?? ''} onChange={(e) => setTargetId(e.target.value || null)}>
              <option value="">Everyone, not a specific target</option>
              {targets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title} ({t.audienceLabel})
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="field">
          <label htmlFor="ses-details">Details (optional)</label>
          <textarea id="ses-details" className="textarea" value={details} onChange={(e) => setDetails(e.target.value)} placeholder="What to bring, or read beforehand" maxLength={2000} />
        </div>
        {error ? <Notice tone="error">{error}</Notice> : null}
        <div className="dialog-actions">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={save.isPending}>
            {session ? 'Save changes' : 'Add session'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function TeamLog() {
  const log = useTeamLog();
  const [downloading, setDownloading] = useState(false);
  if (!log.data) return null;
  const logged = log.data.people.filter((p) => p.minutes > 0);
  return (
    <section className="hub-section">
      <div className="hub-section-head">
        <h2>PD logged this year</h2>
        <p className="muted">
          What staff have recorded doing in the app ({log.data.year}): {hours(log.data.totalMinutes)} in total. Totals only; what they wrote stays with them.
        </p>
      </div>
      {logged.length ? (
        <div>
          <Button
            variant="secondary"
            loading={downloading}
            onClick={async () => {
              setDownloading(true);
              try {
                await downloadFile('/development/team-log.csv', `educaitor-pd-${log.data?.year.replace('/', '-')}.csv`);
              } finally {
                setDownloading(false);
              }
            }}
          >
            Download as CSV
          </Button>
        </div>
      ) : null}
      {logged.length ? (
        <div className="panel hub-table-wrap">
          <table className="hub-table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Entries</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {logged.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td className="hub-num">{p.entries}</td>
                  <td className="hub-num">{hours(p.minutes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="faint">Nobody has logged any PD yet. Teachers log it from My development in the app.</p>
      )}
    </section>
  );
}

// ---------- Creating and editing a target

type AudienceType = Audience['type'];

function TargetEditor({ options, target, onClose }: { options: DevelopmentOptions; target: Target | null; onClose: () => void }) {
  const save = useSaveTarget();
  const [title, setTitle] = useState(target?.title ?? '');
  const [why, setWhy] = useState(target?.why ?? '');
  const [priority, setPriority] = useState<string | null>(target?.priorityText ?? null);
  const [modelId, setModelId] = useState(target?.model?.id ?? options.models[0]?.id ?? 'aar');
  const [indicatorIds, setIndicatorIds] = useState<string[]>(target?.indicators.map((i) => i.id) ?? []);
  const [audienceType, setAudienceType] = useState<AudienceType>(target?.audience.type ?? 'everyone');
  const [audienceValue, setAudienceValue] = useState<string>(
    target && 'value' in target.audience ? target.audience.value : '',
  );
  const [peopleIds, setPeopleIds] = useState<string[]>(target?.audience.type === 'people' ? target.audience.ids : []);
  const [peopleQuery, setPeopleQuery] = useState('');
  const [startsOn, setStartsOn] = useState(target?.startsOn ?? options.term.from);
  const [endsOn, setEndsOn] = useState(target?.endsOn ?? options.term.to);
  const [checkpoints, setCheckpoints] = useState<{ id?: string; label: string; dueOn: string }[]>(
    target?.checkpoints.map((c) => ({ id: c.id, label: c.label, dueOn: c.dueOn })) ?? options.suggestedCheckpoints,
  );
  const [error, setError] = useState<string | null>(null);

  const byDomain = useMemo(() => {
    const groups = new Map<string, { name: string; items: DevelopmentOptions['indicators'] }>();
    for (const i of options.indicators) {
      const g = groups.get(i.domainId) ?? { name: i.domainName, items: [] };
      g.items.push(i);
      groups.set(i.domainId, g);
    }
    return [...groups.values()];
  }, [options.indicators]);

  const pickPriority = (text: string | null) => {
    setPriority(text);
    const p = options.priorities.find((x) => x.text === text);
    if (p) {
      setIndicatorIds(p.indicatorIds.slice(0, 6));
      if (!title.trim()) setTitle(p.text);
    }
  };
  const toggleIndicator = (id: string) =>
    setIndicatorIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : ids.length >= 6 ? ids : [...ids, id]));

  const audience: Audience | null =
    audienceType === 'everyone'
      ? { type: 'everyone' }
      : audienceType === 'people'
        ? peopleIds.length
          ? { type: 'people', ids: peopleIds }
          : null
        : audienceValue
          ? ({ type: audienceType, value: audienceValue } as Audience)
          : null;

  const model = options.models.find((m) => m.id === modelId);
  const valueOptions =
    audienceType === 'career'
      ? options.groups.careerStages.map((c) => ({ value: c.id, label: `${c.label} (${c.people})` }))
      : audienceType === 'subject'
        ? options.groups.subjects.map((s) => ({ value: s.value, label: `${s.value} (${s.people})` }))
        : audienceType === 'age'
          ? options.groups.ages.map((a) => ({ value: a.value, label: `Ages ${a.value} (${a.people})` }))
          : [];

  const problem = !title.trim()
    ? 'Give the target a title.'
    : !audience
      ? 'Choose who it’s for.'
      : !checkpoints.length
        ? 'Add at least one check-in.'
        : endsOn < startsOn
          ? 'The target has to end after it starts.'
          : null;

  const submit = async () => {
    if (problem || !audience) {
      setError(problem);
      return;
    }
    setError(null);
    const input: TargetInput = { title, why, priorityText: priority, reflectionModelId: modelId, audience, indicatorIds, startsOn, endsOn, checkpoints };
    try {
      await save.mutateAsync({ id: target?.id, input });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That didn’t save. Try again.');
    }
  };

  const people = options.groups.people.filter((p) => p.name.toLowerCase().includes(peopleQuery.trim().toLowerCase()));

  return (
    <Dialog open onClose={onClose} title={target ? 'Edit target' : 'New target'} wide>
      <div className="dev-editor">
        {options.priorities.length ? (
          <fieldset className="choice">
            <legend>Start from one of your priority areas (optional)</legend>
            <div className="chips">
              {options.priorities.map((p) => (
                <button key={p.text} type="button" className="choice-chip" aria-pressed={priority === p.text} onClick={() => pickPriority(priority === p.text ? null : p.text)}>
                  {p.text}
                </button>
              ))}
            </div>
          </fieldset>
        ) : null}

        <Field id="dev-title" label="Target" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Questioning that makes every pupil think" maxLength={160} />
        <div className="field">
          <label htmlFor="dev-why">Why it matters (optional)</label>
          <textarea id="dev-why" className="textarea" value={why} onChange={(e) => setWhy(e.target.value)} placeholder="What you hope changes, and why now" maxLength={1000} />
        </div>

        <fieldset className="choice">
          <legend>Who it&rsquo;s for</legend>
          <div className="chips">
            {(
              [
                ['everyone', 'Everyone'],
                ['career', 'A career stage'],
                ['subject', 'A subject'],
                ['age', 'An age group'],
                ['people', 'Chosen people'],
              ] as [AudienceType, string][]
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                className="choice-chip"
                aria-pressed={audienceType === k}
                onClick={() => {
                  setAudienceType(k);
                  setAudienceValue('');
                }}
              >
                {label}
              </button>
            ))}
          </div>
          {valueOptions.length ? (
            <select className="select dev-select" value={audienceValue} onChange={(e) => setAudienceValue(e.target.value)} aria-label="Which group">
              <option value="">Choose…</option>
              {valueOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : audienceType !== 'everyone' && audienceType !== 'people' ? (
            <p className="faint">Nobody has told us this yet in their profile.</p>
          ) : null}
          {audienceType === 'people' ? (
            <div className="dev-people">
              <Field id="dev-people-q" label="Find people" value={peopleQuery} onChange={(e) => setPeopleQuery(e.target.value)} placeholder="Search by name" />
              <div className="dev-people-list">
                {people.map((p) => (
                  <label key={p.id} className="auth-check dev-person">
                    <input
                      type="checkbox"
                      checked={peopleIds.includes(p.id)}
                      onChange={() => setPeopleIds((ids) => (ids.includes(p.id) ? ids.filter((x) => x !== p.id) : [...ids, p.id]))}
                    />
                    <span>{p.name}</span>
                  </label>
                ))}
              </div>
              <p className="faint">{peopleIds.length} chosen</p>
            </div>
          ) : null}
        </fieldset>

        <fieldset className="choice">
          <legend>Tracked through (up to six framework indicators)</legend>
          <div className="dev-indicator-picker">
            {byDomain.map((g) => (
              <div key={g.name}>
                <p className="dev-domain">{g.name}</p>
                <div className="chips">
                  {g.items.map((i) => (
                    <button
                      key={i.id}
                      type="button"
                      className="choice-chip"
                      aria-pressed={indicatorIds.includes(i.id)}
                      disabled={!indicatorIds.includes(i.id) && indicatorIds.length >= 6}
                      onClick={() => toggleIndicator(i.id)}
                    >
                      {i.name}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </fieldset>

        <fieldset className="choice">
          <legend>How people reflect</legend>
          <div className="sm-cards" role="radiogroup" aria-label="Reflection model">
            {options.models.map((m) => (
              <button key={m.id} type="button" role="radio" aria-checked={modelId === m.id} className="sm-card" onClick={() => setModelId(m.id)}>
                <span className="sm-radio" aria-hidden="true" />
                <span>
                  <span className="sm-title">
                    {m.name}
                    {m.source !== 'EducAitor' ? <span className="chip chip-accent">{m.source}</span> : null}
                  </span>
                  {m.summary ? <span className="muted dev-model-summary">{m.summary}</span> : null}
                  {modelId === m.id ? (
                    <ol className="dev-prompts">
                      {m.prompts.map((p) => (
                        <li key={p.key}>{p.question}</li>
                      ))}
                    </ol>
                  ) : null}
                </span>
              </button>
            ))}
          </div>
          {model ? null : <p className="faint">Choose a model.</p>}
        </fieldset>

        <div className="dev-dates">
          <Field id="dev-start" label="Starts" type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
          <Field id="dev-end" label="Ends" type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
        </div>

        <fieldset className="choice">
          <legend>Check-ins (everyone it&rsquo;s for is nudged to reflect on the day)</legend>
          <div className="dev-cp-rows">
            {checkpoints.map((c, i) => (
              <div key={c.id ?? i} className="dev-cp-row">
                <input
                  className="select select-sm"
                  aria-label={`Check-in ${i + 1} name`}
                  value={c.label}
                  maxLength={60}
                  onChange={(e) => setCheckpoints((cs) => cs.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                />
                <input
                  className="select select-sm"
                  type="date"
                  aria-label={`Check-in ${i + 1} date`}
                  value={c.dueOn}
                  onChange={(e) => setCheckpoints((cs) => cs.map((x, j) => (j === i ? { ...x, dueOn: e.target.value } : x)))}
                />
                <button type="button" className="hub-link" onClick={() => setCheckpoints((cs) => cs.filter((_, j) => j !== i))} aria-label={`Remove ${c.label}`}>
                  Remove
                </button>
              </div>
            ))}
          </div>
          {checkpoints.length < 6 ? (
            <button type="button" className="hub-link" onClick={() => setCheckpoints((cs) => [...cs, { label: 'Check-in', dueOn: endsOn }])}>
              Add a check-in
            </button>
          ) : null}
        </fieldset>

        {error ? <Notice tone="error">{error}</Notice> : null}
        <div className="dialog-actions">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={save.isPending}>
            {target ? 'Save changes' : 'Set target'}
          </Button>
        </div>
        {!target ? (
          <p className="faint">
            Everyone it&rsquo;s for sees it in the app straight away. You can change it later; reflections already written stay with the check-in they
            were for.
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
